import styled from 'styled-components'
import { FlexCol, FlexRow } from '@src/styles'

export const GoogleDriveContainer = styled(FlexCol)`
  padding: 24px;
  gap: 24px;
`

export const ConnectionCard = styled.div`
  background: ${props => props.theme.cardBackground || '#1a1a2e'};
  border-radius: 8px;
  padding: 24px;
  border: 1px solid ${props => props.theme.borderColor || '#333'};
`

export const ConnectionHeader = styled(FlexRow)`
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

export const ConnectionTitle = styled.h2`
  color: ${props => props.theme.white};
  margin: 0;
  font-size: 1.25rem;
`

export const ConnectionStatus = styled.div<{ connected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.connected ? '#4CAF50' : props.theme.textMuted || '#888'};
  font-size: 0.875rem;
`

export const StatusDot = styled.span<{ connected?: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.connected ? '#4CAF50' : '#888'};
`

export const ConnectedEmail = styled.span`
  color: ${props => props.theme.textMuted || '#aaa'};
  font-size: 0.875rem;
`

export const SyncConfigSection = styled(FlexCol)`
  gap: 16px;
  margin-top: 24px;
`

export const SyncConfigCard = styled.div`
  background: ${props => props.theme.cardBackgroundDark || '#141428'};
  border-radius: 8px;
  padding: 16px;
  border: 1px solid ${props => props.theme.borderColor || '#333'};
`

export const SyncConfigHeader = styled(FlexRow)`
  justify-content: space-between;
  align-items: center;
`

export const FolderName = styled.span`
  color: ${props => props.theme.white};
  font-weight: 500;
`

export const TargetPath = styled.span`
  color: ${props => props.theme.textMuted || '#888'};
  font-size: 0.875rem;
`

export const SyncStatus = styled.span<{ hasError?: boolean }>`
  color: ${props => props.hasError ? '#f44336' : props.theme.textMuted || '#888'};
  font-size: 0.75rem;
`

export const FolderPickerContainer = styled(FlexCol)`
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid ${props => props.theme.borderColor || '#333'};
  border-radius: 8px;
  padding: 8px;
`

export const FolderItem = styled(FlexRow)<{ selected?: boolean }>`
  padding: 12px;
  cursor: pointer;
  border-radius: 4px;
  align-items: center;
  gap: 12px;
  background: ${props => props.selected ? 'rgba(76, 175, 80, 0.1)' : 'transparent'};

  &:hover {
    background: ${props => props.selected ? 'rgba(76, 175, 80, 0.15)' : 'rgba(255, 255, 255, 0.05)'};
  }
`

export const FolderIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.primary || '#4285f4'};
`

export const FolderLabel = styled.span`
  color: ${props => props.theme.white};
`

export const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${props => {
    switch (props.variant) {
      case 'danger':
        return `
          background: #f44336;
          color: white;
          &:hover { background: #d32f2f; }
        `
      case 'secondary':
        return `
          background: transparent;
          color: ${props.theme.white};
          border: 1px solid ${props.theme.borderColor || '#333'};
          &:hover { background: rgba(255, 255, 255, 0.05); }
        `
      default:
        return `
          background: ${props.theme.primary || '#4285f4'};
          color: white;
          &:hover { background: ${props.theme.primaryDark || '#3367d6'}; }
        `
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const ButtonGroup = styled(FlexRow)`
  gap: 12px;
`

export const SyncHistoryContainer = styled(FlexCol)`
  gap: 8px;
  margin-top: 16px;
`

export const SyncHistoryItem = styled(FlexRow)`
  padding: 12px;
  background: ${props => props.theme.cardBackgroundDark || '#141428'};
  border-radius: 6px;
  justify-content: space-between;
  align-items: center;
`

export const SyncHistoryStatus = styled.span<{ status: string }>`
  color: ${props => {
    switch (props.status) {
      case 'completed': return '#4CAF50'
      case 'failed': return '#f44336'
      case 'in_progress': return '#ff9800'
      default: return '#888'
    }
  }};
  font-weight: 500;
  font-size: 0.875rem;
`

export const SyncHistoryStats = styled.span`
  color: ${props => props.theme.textMuted || '#888'};
  font-size: 0.75rem;
`

export const ErrorMessage = styled.div`
  color: #f44336;
  background: rgba(244, 67, 54, 0.1);
  padding: 12px;
  border-radius: 6px;
  font-size: 0.875rem;
`

export const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${props => props.theme.textMuted || '#888'};
`

export const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid ${props => props.theme.borderColor || '#333'};
  border-top-color: ${props => props.theme.primary || '#4285f4'};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`

export const InputField = styled.input`
  padding: 12px;
  border-radius: 6px;
  border: 1px solid ${props => props.theme.borderColor || '#333'};
  background: ${props => props.theme.cardBackgroundDark || '#141428'};
  color: ${props => props.theme.white};
  font-size: 0.875rem;
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary || '#4285f4'};
  }

  &::placeholder {
    color: ${props => props.theme.textMuted || '#888'};
  }
`

export const Label = styled.label`
  color: ${props => props.theme.white};
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 8px;
`

export const FormGroup = styled(FlexCol)`
  gap: 8px;
`

export const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  cursor: pointer;
`

export const CheckboxLabel = styled(FlexRow)`
  align-items: center;
  gap: 8px;
  color: ${props => props.theme.white};
  font-size: 0.875rem;
  cursor: pointer;
`
